"use client";

import { useLocale } from "next-intl";
import { FC } from "react";

import { ButtonRecord as ButtonRecordType, IButton, IconType } from "@types";
import { Locale } from "consts/locale";
import { useOfficeContactPrefillQuery } from "context";
import { getLink, withOfficePrefillOnGeneralContactLink } from "utils/link";
import { type OfficeContactPrefillParams } from "utils/officeContact";

import {
  ButtonExternalRecord,
  ButtonRecord as DatoButtonRecord,
  HubspotButtonRecord,
} from "@graphql/types/graphql";
import { Button } from "components/atoms/Button/Button";
import { DownloadButton } from "components/atoms/DownloadButton/DownloadButton";
import { HubspotButton } from "components/atoms/HubspotButton/HubspotButton";

export function getButtonPropsFromDatoButton(
  { __typename, ...props }: ButtonRecordType,
  locale: Locale,
  officeContactPrefill?: OfficeContactPrefillParams,
): IButton | null {
  if (__typename === "ButtonExternalRecord") {
    const item = props as ButtonExternalRecord;
    return {
      icon: (item.icon as IconType) ?? IconType.CaretFilled,
      preIcon: item.preIcon ?? false,
      label: item.text,
      link: { href: item.link },
      theme: item.textual ? "textual" : "dark",
      openLinkInNewTab: !!item.openLinkInNewTab,
    };
  }
  if (__typename === "ButtonRecord") {
    const item = props as DatoButtonRecord;
    let link = getLink(item.link, locale);
    if (officeContactPrefill) {
      link = withOfficePrefillOnGeneralContactLink(link, officeContactPrefill);
    }
    return {
      icon: (item.icon as IconType) ?? IconType.CaretFilled,
      preIcon: item.preIcon ?? false,
      label: item.text,
      link,
      theme: item.textual ? "textual" : "dark",
      openLinkInNewTab: !!item.openLinkInNewTab,
    };
  }
  if (__typename === "HubspotButtonRecord") {
    const item = props as HubspotButtonRecord;
    return {
      icon: (item.icon as IconType) ?? IconType.CaretFilled,
      preIcon: item.preIcon ?? false,
      text: item.text,
      label: item.text,
      theme: item.textual ? "textual" : "dark",
      hubspotCodeSnippet: item.hubspotCodeSnippet,
    };
  }

  // DownloadButtonRecord needs special handling - return null
  if (__typename === "DownloadButtonRecord") {
    return null;
  }
  return null;
}

export const CBButton: FC<ButtonRecordType> = (buttonRecord) => {
  const locale = useLocale() as Locale;
  const officeContactPrefill = useOfficeContactPrefillQuery();

  // Special handling for DownloadButtonRecord
  if (buttonRecord.__typename === "DownloadButtonRecord") {
    return <DownloadButton {...buttonRecord} />;
  }

  // Special handling for HubspotButtonRecord
  if (buttonRecord.__typename === "HubspotButtonRecord") {
    const props = getButtonPropsFromDatoButton(buttonRecord, locale, officeContactPrefill);
    return props ? <HubspotButton {...props} /> : null;
  }

  // Use the helper function to get button props
  const buttonProps = getButtonPropsFromDatoButton(buttonRecord, locale, officeContactPrefill);

  if (!buttonProps) {
    return null;
  }

  return <Button {...buttonProps} />;
};
